// src/components/admin/AdminMatches.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "../../utils/axiosConfig";
import {
  Plus, Ticket, Calendar, MapPin, Trophy, QrCode,
  Edit3, Trash2, X, Check, Zap, DollarSign, Users, ArrowLeft,
  Shield
} from "lucide-react";

const STATUS_LABELS = {
  "Programado": { label: "Programado", color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  "En Juego": { label: "En Juego", color: "text-green-400 bg-green-500/10 border-green-500/20 animate-pulse" },
  "Finalizado": { label: "Finalizado", color: "text-white/40 bg-white/5 border-white/10" },
  "Suspendido": { label: "Suspendido", color: "text-red-400 bg-red-500/10 border-red-500/20" },
};

const EMPTY_FORM = {
  matchName: "",
  teamAName: "",
  teamBName: "",
  date: "",
  location: "",
  isTicketed: false,
  ticketPrice: "",
  totalTickets: "",
};

export default function AdminMatches() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchMatches = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/matches/admin/all`);
      setMatches(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMatches(); }, [fetchMatches]);

  const openCreate = () => {
    setEditingMatch(null);
    setForm(EMPTY_FORM);
    setError("");
    setShowForm(true);
  };

  const openEdit = (match) => {
    setEditingMatch(match);
    setForm({
      matchName: match.matchName || "",
      teamAName: match.teamAName || match.teamA?.name || "",
      teamBName: match.teamBName || match.teamB?.name || "",
      date: match.date ? new Date(match.date).toISOString().slice(0, 16) : "",
      location: match.location || "",
      isTicketed: match.isTicketed || false,
      ticketPrice: match.ticketPrice ?? "",
      totalTickets: match.totalTickets ?? "",
    });
    setError("");
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditingMatch(null); };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.teamAName.trim() || !form.teamBName.trim()) {
      setError("Ingresá los nombres de ambos equipos.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        matchName: form.matchName,
        teamAName: form.teamAName,
        teamBName: form.teamBName,
        date: form.date || null,
        location: form.location,
        isTicketed: form.isTicketed,
        ticketPrice: form.isTicketed ? Number(form.ticketPrice) : 0,
        totalTickets: form.isTicketed ? Number(form.totalTickets) : 0,
      };
      if (editingMatch) {
        await axios.put(`/api/matches/${editingMatch._id}`, payload);
      } else {
        await axios.post(`/api/matches`, payload);
      }
      closeForm();
      fetchMatches();
    } catch (err) {
      setError(err?.response?.data?.error || "Error al guardar el partido.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (matchId) => {
    if (!window.confirm("¿Eliminar este partido?")) return;
    try {
      await axios.delete(`/api/matches/${matchId}`);
      fetchMatches();
    } catch (e) {
      alert("Error al eliminar.");
    }
  };

  const upcoming = matches.filter(m => m.status !== "Finalizado");
  const finished = matches.filter(m => m.status === "Finalizado");

  return (
    <div className="min-h-screen bg-[#080808] text-white px-4 py-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/${slug}/admin/dashboard`)}
            className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-all"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-3xl font-black tracking-tight">Gestión de Partidos</h1>
            <p className="text-white/40 text-sm font-medium">{matches.length} partido{matches.length !== 1 ? "s" : ""} en total</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-green-500 hover:bg-green-400 text-black px-5 py-3 rounded-2xl font-black text-sm transition-all shadow-lg shadow-green-500/20"
        >
          <Plus size={18} />
          Nuevo Partido
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-24">
          <div className="w-8 h-8 border-2 border-green-500/30 border-t-green-500 rounded-full animate-spin" />
        </div>
      ) : matches.length === 0 ? (
        <div className="bg-white/[0.02] border border-dashed border-white/10 rounded-3xl p-16 text-center">
          <Trophy size={48} className="mx-auto mb-4 text-white/15" />
          <p className="text-white/40 font-bold text-lg mb-2">No hay partidos todavía</p>
          <p className="text-white/20 text-sm mb-6">Creá tu primer partido con el botón de arriba</p>
          <button onClick={openCreate} className="bg-green-500 text-black px-6 py-3 rounded-2xl font-black text-sm hover:bg-green-400 transition-all">
            + Crear partido
          </button>
        </div>
      ) : (
        <div className="space-y-10">
          {/* Upcoming */}
          {upcoming.length > 0 && (
            <div>
              <p className="text-[10px] font-black text-white/30 uppercase tracking-[3px] mb-4">Próximos y en curso</p>
              <div className="space-y-3">
                {upcoming.map(match => <MatchCard key={match._id} match={match} slug={slug} onEdit={openEdit} onDelete={handleDelete} />)}
              </div>
            </div>
          )}
          {/* Finished */}
          {finished.length > 0 && (
            <div>
              <p className="text-[10px] font-black text-white/30 uppercase tracking-[3px] mb-4">Finalizados</p>
              <div className="space-y-3 opacity-60">
                {finished.map(match => <MatchCard key={match._id} match={match} slug={slug} onEdit={openEdit} onDelete={handleDelete} />)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* FORM MODAL */}
      {showForm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" onClick={closeForm}>
          <div
            onClick={e => e.stopPropagation()}
            className="w-full max-w-lg max-h-[92vh] flex flex-col bg-[#0A0A0A] border border-white/10 rounded-[40px] shadow-2xl animate-fade-in-up overflow-hidden relative"
          >
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-green-500/20 rounded-full blur-[80px] pointer-events-none" />

            <div className="overflow-y-auto flex-1">
              <div className="px-8 pt-8 pb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/20">
                    <Trophy size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-white leading-none">
                      {editingMatch ? "Editar Partido" : "Nuevo Partido"}
                    </h3>
                    <p className="text-[10px] text-white/30 uppercase tracking-widest font-bold mt-0.5">Sin torneo asociado</p>
                  </div>
                </div>
                <button onClick={closeForm} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white transition-all">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSave} className="px-8 pb-8 space-y-5">
                {/* Match Name (optional) */}
                <div>
                  <label className="block text-[10px] font-black text-white/30 uppercase tracking-[3px] mb-2">Nombre del partido (opcional)</label>
                  <input
                    name="matchName"
                    value={form.matchName}
                    onChange={handleChange}
                    placeholder="Ej: Clásico, Definición del Torneo..."
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-3.5 text-white font-medium placeholder:text-white/20 outline-none focus:border-green-500/50 focus:ring-4 focus:ring-green-500/10 transition-all"
                  />
                </div>

                {/* Teams — Free text */}
                <div>
                  <label className="block text-[10px] font-black text-white/30 uppercase tracking-[3px] mb-3">Equipos *</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                        <Shield size={14} className="text-white/20" />
                      </div>
                      <input
                        name="teamAName"
                        value={form.teamAName}
                        onChange={handleChange}
                        placeholder="Equipo A"
                        required
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-9 pr-4 py-3.5 text-white font-bold placeholder:text-white/20 outline-none focus:border-green-500/50 focus:ring-4 focus:ring-green-500/10 transition-all"
                      />
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                        <Shield size={14} className="text-white/20" />
                      </div>
                      <input
                        name="teamBName"
                        value={form.teamBName}
                        onChange={handleChange}
                        placeholder="Equipo B"
                        required
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-9 pr-4 py-3.5 text-white font-bold placeholder:text-white/20 outline-none focus:border-green-500/50 focus:ring-4 focus:ring-green-500/10 transition-all"
                      />
                    </div>
                  </div>
                  <p className="text-center text-[10px] text-white/20 font-bold uppercase tracking-widest mt-2">vs</p>
                </div>

                {/* Date & Location */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-black text-white/30 uppercase tracking-[3px] mb-2">Fecha y Hora</label>
                    <input
                      type="datetime-local"
                      name="date"
                      value={form.date}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white font-medium outline-none focus:border-green-500/50 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-white/30 uppercase tracking-[3px] mb-2">Ubicación</label>
                    <input
                      name="location"
                      value={form.location}
                      onChange={handleChange}
                      placeholder="Cancha principal..."
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white font-medium placeholder:text-white/20 outline-none focus:border-green-500/50 transition-all"
                    />
                  </div>
                </div>

                {/* Tickets toggle */}
                <div className={`border-2 rounded-2xl p-5 transition-all ${form.isTicketed ? "bg-green-500/5 border-green-500/30" : "bg-white/[0.02] border-white/10"}`}>
                  <label className="flex items-center gap-3 cursor-pointer" onClick={() => setForm(f => ({ ...f, isTicketed: !f.isTicketed }))}>
                    <div className={`w-12 h-6 rounded-full relative transition-colors shrink-0 ${form.isTicketed ? "bg-green-500" : "bg-white/10"}`}>
                      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.isTicketed ? "translate-x-6" : "translate-x-0.5"}`} />
                    </div>
                    <div>
                      <p className={`font-black text-sm ${form.isTicketed ? "text-green-400" : "text-white/50"}`}>Venta de Entradas</p>
                      <p className="text-[11px] text-white/30 font-medium">Los espectadores podrán comprar tickets online</p>
                    </div>
                  </label>

                  {form.isTicketed && (
                    <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-white/5">
                      <div>
                        <label className="block text-[10px] font-black text-white/30 uppercase tracking-[3px] mb-2">Precio por Entrada ($)</label>
                        <input
                          type="number"
                          name="ticketPrice"
                          value={form.ticketPrice}
                          onChange={handleChange}
                          min="0"
                          placeholder="0"
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-bold placeholder:text-white/20 outline-none focus:border-green-500/50 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-white/30 uppercase tracking-[3px] mb-2">Capacidad Total</label>
                        <input
                          type="number"
                          name="totalTickets"
                          value={form.totalTickets}
                          onChange={handleChange}
                          min="0"
                          placeholder="0"
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-bold placeholder:text-white/20 outline-none focus:border-green-500/50 transition-all"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 text-red-400 text-sm font-bold flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 py-5 rounded-2xl font-black text-white shadow-lg shadow-green-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:hover:scale-100"
                >
                  {saving ? (
                    <><div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" /><span>Guardando...</span></>
                  ) : (
                    <><Check size={18} /><span>{editingMatch ? "Guardar Cambios" : "Crear Partido"}</span></>
                  )}
                </button>
              </form>
            </div>

            <div className="px-8 py-5 bg-white/[0.02] border-t border-white/5 flex justify-center">
              <button onClick={closeForm} className="w-full bg-white/5 hover:bg-white/10 border border-white/10 py-3.5 rounded-[18px] font-black text-xs text-white uppercase tracking-[2px] transition-all">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MatchCard({ match, slug, onEdit, onDelete }) {
  const status = STATUS_LABELS[match.status] || STATUS_LABELS["Programado"];
  const teamA = match.teamAName || match.teamA?.name || "Por definir";
  const teamB = match.teamBName || match.teamB?.name || "Por definir";
  const soldPct = match.isTicketed && match.totalTickets > 0
    ? Math.round((match.soldTickets || 0) / match.totalTickets * 100)
    : null;

  return (
    <div className="bg-[#111] border border-white/5 hover:border-green-500/10 rounded-3xl p-6 transition-all group">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Status + name */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full border ${status.color}`}>
              {status.label}
            </span>
            {match.matchName && (
              <span className="text-[10px] font-bold text-white/30 uppercase tracking-widest truncate">{match.matchName}</span>
            )}
          </div>

          {/* Teams scoreline */}
          <div className="flex items-center gap-3 mb-3">
            <span className="font-black text-xl text-white truncate">{teamA}</span>
            {match.status === "Finalizado" ? (
              <span className="font-black text-primary-orange text-xl shrink-0">{match.scoreA} - {match.scoreB}</span>
            ) : (
              <span className="text-white/30 font-bold text-sm shrink-0">vs</span>
            )}
            <span className="font-black text-xl text-white truncate">{teamB}</span>
          </div>

          {/* Meta */}
          <div className="flex flex-wrap gap-4 text-xs text-white/30 font-medium">
            {match.date && (
              <span className="flex items-center gap-1.5">
                <Calendar size={11} />
                {new Date(match.date).toLocaleString("es-AR", { dateStyle: "medium", timeStyle: "short" })}
              </span>
            )}
            {match.location && (
              <span className="flex items-center gap-1.5">
                <MapPin size={11} />
                {match.location}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {match.isTicketed && (
            <Link
              to={`/${slug}/matches/${match._id}/live-access`}
              className="flex items-center gap-1.5 bg-green-500/10 border border-green-500/20 text-green-400 hover:bg-green-500/20 px-3 py-2 rounded-xl text-xs font-black transition-all"
            >
              <QrCode size={13} />
              Kiosk
            </Link>
          )}
          <button onClick={() => onEdit(match)} className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-all">
            <Edit3 size={14} />
          </button>
          <button onClick={() => onDelete(match._id)} className="w-9 h-9 rounded-xl bg-red-500/5 border border-red-500/10 flex items-center justify-center text-red-400/40 hover:text-red-400 hover:bg-red-500/10 transition-all">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Ticket stats */}
      {match.isTicketed && (
        <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-3 gap-3">
          <div className="text-center">
            <p className="text-green-400 font-black">${(match.ticketPrice || 0).toLocaleString("es-AR")}</p>
            <p className="text-[10px] text-white/20 font-bold uppercase tracking-wider">Por entrada</p>
          </div>
          <div className="text-center">
            <p className="text-blue-400 font-black">{match.soldTickets || 0}/{match.totalTickets}</p>
            <p className="text-[10px] text-white/20 font-bold uppercase tracking-wider">Vendidas</p>
          </div>
          <div className="text-center">
            <p className="text-primary-orange font-black">${((match.soldTickets || 0) * (match.ticketPrice || 0)).toLocaleString("es-AR")}</p>
            <p className="text-[10px] text-white/20 font-bold uppercase tracking-wider">Recaudado</p>
          </div>
          {soldPct !== null && (
            <div className="col-span-3">
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full transition-all" style={{ width: `${soldPct}%` }} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
